<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { generateCsv, formatFirebaseTimestamp } from '@/helpers';
import UserService from '@/service/user.service';
import RequestService from '@/service/request.service';
import LoadingSpinner from '@/components/admin-dashboard/LoadingSpinner.vue';
import InquiryService, { type PartnerInquiry } from '@/service/inquiry.service';

const router = useRouter();

const users = ref<any[]>([]);
const usersCount = ref(0);
const requestsCount = ref(0);
const loading = ref(true);
const pageSize = 100;
const currentPage = ref(1);
const lastDocs = ref<any[]>([null]); // Store lastDoc for each page (0 = null for first page)
const totalPages = ref(1);
const paidRequestsCount = ref(0);
const inquiries = ref<PartnerInquiry[]>([]);
const loadingInquiries = ref(false);

async function fetchStats() {
  loading.value = true;
  usersCount.value = await UserService.getUsersCount();
  requestsCount.value = await RequestService.getRequestsCount();
  paidRequestsCount.value = await RequestService.getPaidRequestsCount();
  totalPages.value = Math.max(1, Math.ceil(usersCount.value / pageSize));
  loading.value = false;
}

async function fetchUsers(page: number) {
  loading.value = true;
  let lastDoc = null;
  if (page > 1) {
    lastDoc = lastDocs.value[page - 1];
  }
  const { users: pageUsers, lastDoc: newLastDoc } = await UserService.getUsersPaginated(pageSize, lastDoc);
  users.value = pageUsers;
  // Store lastDoc for this page for navigation
  lastDocs.value[page] = newLastDoc;
  loading.value = false;
}

onMounted(async () => {
  // if (!localStorage.getItem('loggedIn')) {
  //   router.push({ name: 'admin-login' });
  //   return;
  // }
  await fetchStats();
  await fetchUsers(1);
  loadingInquiries.value = true;
  inquiries.value = await InquiryService.getPartnerInquiries();
  loadingInquiries.value = false;
});

async function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
  await fetchUsers(page);
}

async function downloadCsv(collection: string) {
  await generateCsv();
}
</script>
<template>
  <div class="min-h-screen bg-black-primary text-white font-inter p-6">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 class="text-3xl md:text-4xl font-bold text-gold mb-2 animate-fade-in">
            Admin Dashboard
          </h1>
          <p class="text-lg text-gray-300 animate-fade-in-slow">
            Manage users, requests, and platform data.
          </p>
        </div>
        <img
          src="@/assets/gold-logo-transparent.png"
          alt="TALAB Logo"
          class="w-24 md:w-32 animate-fade-in"
        />
      </div>

      <!-- Actions -->
      <div class="flex flex-wrap gap-3 mb-6">
        <button
          @click="router.push({ name: 'partners-overview' })"
          class="px-4 py-2 rounded-lg bg-gold-primary text-black font-semibold hover:bg-gold-secondary transition border border-gold-secondary"
        >
          Open Partners Overview
        </button>
        <button
          @click="router.push({ name: 'admin-dashboard-kpis' })"
          class="px-4 py-2 rounded-lg bg-black-secondary text-gold border border-gold-secondary font-semibold hover:bg-gold-primary hover:text-black transition"
        >
          Open KPIs & Goals
        </button>
        <button
          @click="router.push({ name: 'admin-dashboard-workspace' })"
          class="px-4 py-2 rounded-lg bg-black-secondary text-gold border border-gold-secondary font-semibold hover:bg-gold-primary hover:text-black transition"
        >
          Open Workspace
        </button>
      </div>

      <!-- Stat Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <div class="bg-gradient-to-br from-[#BC8809] to-gold-customYellow rounded-2xl p-6 shadow-lg flex flex-col items-center justify-center animate-pop-in min-h-[120px]">
          <span class="text-4xl font-bold flex items-center justify-center">
            {{ usersCount }}
          </span>
          <span class="text-lg font-medium mt-2">User Accounts</span>
        </div>
        <div class="bg-gradient-to-br from-[#BC8809] to-gold-customYellow rounded-2xl p-6 shadow-lg flex flex-col items-center animate-pop-in delay-100 min-h-[120px]">
          <span class="text-4xl font-bold flex items-center justify-center">
            {{ requestsCount }}
          </span>
          <span class="text-lg font-medium mt-2">Requests</span>
        </div>
        <div class="bg-gradient-to-br from-[#BC8809] to-gold-customYellow rounded-2xl p-6 shadow-lg flex flex-col items-center animate-pop-in delay-200 min-h-[120px]">
          <span class="text-4xl font-bold flex items-center justify-center">
            {{ ((paidRequestsCount / (usersCount || 1)) * 100).toFixed(2) }}
            <span class="ml-1 text-2xl">%</span>
          </span>
          <span class="text-lg font-medium mt-2">Request/User Ratio</span>
        </div>
      </div>

      <!-- Inquiries Section -->
      <div class="mb-10">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-2xl font-semibold text-gold">Partner Inquiries</h2>
        </div>
        <div class="bg-black-secondary rounded-2xl p-4">
          <div v-if="loadingInquiries" class="flex items-center justify-center py-8">
            <LoadingSpinner />
          </div>
          <div v-else>
            <div v-if="inquiries.length === 0" class="text-center text-gray-400 py-8">No inquiries yet.</div>
            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div v-for="inq in inquiries" :key="inq.id" class="bg-black-primary/70 border border-zinc-800 rounded-xl p-4">
                <div class="flex items-start justify-between mb-2">
                  <div>
                    <div class="text-lg font-semibold">{{ inq.name }}</div>
                    <div class="text-sm text-gray-400">{{ inq.email }}</div>
                  </div>
                  <span class="text-xs px-2 py-1 rounded-full border" :class="{
                      'text-yellow-300 border-yellow-300': inq.status==='new',
                      'text-blue-300 border-blue-300': inq.status==='contacted',
                      'text-green-300 border-green-300': inq.status==='qualified',
                      'text-gray-300 border-gray-300': inq.status==='closed',
                  }">{{ inq.status }}</span>
                </div>
                <div class="text-sm text-gray-300">
                  <div v-if="inq.company">Company: {{ inq.company }}</div>
                  <div v-if="inq.phone">Phone: {{ inq.phone }}</div>
                </div>
                <div v-if="inq.message" class="mt-2 text-sm text-gray-400 line-clamp-3">{{ inq.message }}</div>
                <div class="mt-3 text-xs text-gray-500">
                  Submitted: {{ inq.createdAt ? formatFirebaseTimestamp(inq.createdAt as any, true) : '—' }}
                </div>
                <div class="mt-3 flex items-center gap-2">
                  <button class="text-xs px-3 py-1 rounded bg-gold-primary text-black hover:bg-gold-secondary" @click="InquiryService.updateInquiryStatus(inq.id!, 'contacted').then(()=>inq.status='contacted')">Mark Contacted</button>
                  <button class="text-xs px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-500" @click="InquiryService.updateInquiryStatus(inq.id!, 'qualified').then(()=>inq.status='qualified')">Qualify</button>
                  <button class="text-xs px-3 py-1 rounded bg-zinc-700 text-white hover:bg-zinc-600" @click="InquiryService.updateInquiryStatus(inq.id!, 'closed').then(()=>inq.status='closed')">Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16">
        <LoadingSpinner />
        <div class="w-full max-w-2xl mt-8">
          <div class="h-8 bg-zinc-800 rounded mb-4 animate-pulse"></div>
          <div class="h-8 bg-zinc-800 rounded mb-4 animate-pulse"></div>
          <div class="h-8 bg-zinc-800 rounded mb-4 animate-pulse"></div>
        </div>
      </div>

      <!-- User Table -->
      <div v-else>
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-2xl font-semibold text-gold">User Accounts</h2>
          <button
            @click="downloadCsv('users')"
            class="text-lg text-gold-primary border border-gold-primary px-4 py-2 rounded-lg hover:bg-gold-primary hover:text-black transition"
          >
            Download users (csv)
          </button>
        </div>
        <div class="overflow-x-auto rounded-2xl shadow-lg bg-black-secondary">
          <table class="w-full text-sm text-white table-auto animate-fade-in">
            <thead class="text-xs uppercase bg-black-primary/90 text-gold sticky top-0 z-10">
              <tr>
                <th class="px-4 py-3">ID</th>
                <th class="px-4 py-3">Fullname</th>
                <th class="px-4 py-3">Email</th>
                <th class="px-4 py-3">University</th>
                <th class="px-4 py-3">Phone</th>
                <th class="px-4 py-3">Registration Date</th>
                <th class="px-4 py-3">Referred By</th>
                <th class="px-4 py-3">Platform</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="user in users"
                :key="user.id"
                class="odd:bg-black-primary/80 even:bg-black-primary/60 border-b border-zinc-800 transition-all duration-300 hover:bg-gold-secondary/10 animate-pop-in"
              >
                <td class="border px-4 py-2">{{ user.id }}</td>
                <td class="border px-4 py-2">{{ user.fullName }}</td>
                <td class="border px-4 py-2">{{ user.email }}</td>
                <td class="border px-4 py-2">{{ user.university }}</td>
                <td class="border px-4 py-2">{{ user.phoneNumber }}</td>
                <td class="border px-4 py-2">
                  {{ formatFirebaseTimestamp(user.registrationDate, true) }}
                </td>
                <td class="border px-4 py-2">
                  {{
                    user.referredBy
                      ? users.find((x) => x.userId === user.referredBy)
                          ?.fullName || "none"
                      : "none"
                  }}
                </td>
                <td class="border px-4 py-2">{{ user.platform }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="users.length === 0" class="text-center text-gray-400 py-8">
            No users found.
          </div>
        </div>
        <!-- Pagination Controls -->
        <div v-if="totalPages > 1" class="flex justify-center items-center gap-2 mt-6">
          <button
            class="px-3 py-1 rounded bg-black-secondary border border-gold-primary text-gold hover:bg-gold-primary hover:text-black transition"
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
          >
            Prev
          </button>
          <span class="mx-2 text-gold"
            >Page {{ currentPage }} of {{ totalPages }}</span
          >
          <button
            class="px-3 py-1 rounded bg-black-secondary border border-gold-primary text-gold hover:bg-gold-primary hover:text-black transition"
            :disabled="currentPage === totalPages"
            @click="goToPage(currentPage + 1)"
          >
            Next
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<style lang="scss" scoped>
.font-inter {
  font-family: "Inter", sans-serif;
}
.text-gold {
  @apply text-gold-primary;
}
.bg-black-primary {
  background: #181818;
}
.bg-black-secondary {
  background: #0C0C0C;
}
.animate-fade-in {
  animation: fadeIn 1s ease;
}
.animate-fade-in-slow {
  animation: fadeIn 1.5s ease;
}
.animate-pop-in {
  animation: popIn 0.7s cubic-bezier(0.68, -0.55, 0.27, 1.55);
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes popIn {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  80% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
